import Sidebar from '@apps-common/ui-components/Sidebar/Sidebar.jsx';
import SidebarHeader from '@apps-common/ui-components/Sidebar/SidebarHeader.jsx';
import SidebarItem from '@apps-common/ui-components/Sidebar/SidebarItem.jsx';
import MosaicsIcon from '@apps-common/ui-icons/Mosaics.jsx';
import AreasOfInterestIcon from '@apps-common/ui-icons/Polygon.jsx';
import SavedSearchIcon from '@apps-common/ui-icons/SavedSearch.jsx';
import SubscribeIcon from '@apps-common/ui-icons/Subscribe.jsx';
import SubscriptionsIcon from '@apps-common/ui-icons/Subscriptions.jsx';
import OverviewIcon from '@mui/icons-material/AutoAwesomeMosaic';
import DestinationsIcon from '@mui/icons-material/Hub';
import BrowserIcon from '@mui/icons-material/Language';
import DataCollectionsIcon from '@mui/icons-material/StorageOutlined';
import OrdersIcon from '@mui/icons-material/TextSnippetOutlined';
import SearchAndOrderIcon from '@mui/icons-material/TravelExploreOutlined';
import {EVENT_ACTIONS, trackEvent} from '@src/analytics.js';
import {APP_LINKS} from '@src/config.ts';
import {
  ANALYZE_PATH_PREFIX,
  ANALYZE_ROUTE_KEYS,
} from '@src/constants/routes.ts';
import useMapLocationParams from '@src/hooks/queryParams/useMapLocationParams';
import {useFlags} from '@src/hooks/useFlags';
import {Link, useLocation} from 'react-router';

const LocalSidebar = props => {
  const location = useLocation();
  const flags = useFlags();
  const {featuresManagerIntegration} = flags;
  const {serialize, zoom, center} = useMapLocationParams();

  const queryParams = serialize({zoom, center});

  const navigationItems = [
    {
      label: 'Overview',
      icon: OverviewIcon,
      to: '/overview',
      component: Link,
    },
    {
      type: 'header',
      label: 'Add data',
    },
    {
      label: 'Search and order',
      icon: SearchAndOrderIcon,
      href: APP_LINKS.explorer,
    },
    {
      label: 'Subscribe to data',
      icon: SubscribeIcon,
      to: {
        pathname: '/subscriptions/new',
        search: queryParams,
      },
      component: Link,
    },
    featuresManagerIntegration
      ? {
          label: 'Areas of interest',
          icon: AreasOfInterestIcon,
          to: {
            pathname: '/aois',
            search: queryParams,
          },
          component: Link,
          flags: ['showMapRoutes'],
        }
      : {
          label: 'Areas of interest',
          icon: AreasOfInterestIcon,
          href: APP_LINKS.featuresManager,
        },
    {
      type: 'header',
      label: 'View data delivery',
    },
    {
      label: 'Orders',
      icon: OrdersIcon,
      to: '/orders',
      component: Link,
    },
    {
      label: 'Subscriptions',
      icon: SubscriptionsIcon,
      to: '/subscriptions',
      component: Link,
      selectedFn: location =>
        location.pathname.includes('/subscriptions') &&
        location.pathname !== '/subscriptions/new',
    },
    {
      label: 'Data collections',
      icon: DataCollectionsIcon,
      href: APP_LINKS.dataCollections,
    },
    {
      label: 'Mosaics',
      icon: MosaicsIcon,
      to: {
        pathname: '/mosaics',
        search: queryParams,
      },
      component: Link,
    },
    {
      label: 'Destinations',
      icon: DestinationsIcon,
      to: '/destinations',
      component: Link,
    },
    {
      type: 'header',
      label: 'Analyze Data',
      flags: ['showBrowser'],
    },
    {
      label: 'Visual analysis (BETA)',
      icon: BrowserIcon,
      to: {
        pathname: `${ANALYZE_PATH_PREFIX}/${ANALYZE_ROUTE_KEYS.BROWSE}`,
        search: queryParams,
      },
      component: Link,
      flags: ['showBrowser'],
      selectedFn: location =>
        location.pathname.startsWith(`${ANALYZE_PATH_PREFIX}/`),
    },
    {
      type: 'header',
      label: 'Experiments (internal only)',
      flags: ['showMapRoutes'],
    },
    {
      label: 'Explorer search (dev)',
      icon: SearchAndOrderIcon,
      to: {
        pathname: '/explorer',
        search: queryParams,
      },
      component: Link,
      flags: ['showMapRoutes'],
    },
    {
      label: 'Saved searches (dev)',
      icon: SavedSearchIcon,
      to: {
        pathname: '/saved-searches',
        search: queryParams,
      },
      component: Link,
      flags: ['showMapRoutes'],
    },
    {
      label: 'Areas of interest (dev)',
      icon: AreasOfInterestIcon,
      to: {
        pathname: '/aois',
        search: queryParams,
      },
      component: Link,
      flags: ['showMapRoutes'],
      hideIfFlag: 'featuresManagerIntegration',
    },
    {
      label: 'Data collections (dev)',
      icon: DataCollectionsIcon,
      to: '/collections',
      component: Link,
      flags: ['showDataCollectionsFeature'],
    },
  ];

  return (
    <Sidebar {...props}>
      {navigationItems.map(item => {
        const hasFlags = !!item.flags && item.flags.length > 0;
        const isFlagged = (item.flags ?? []).some(flag => flags[flag]);

        // If the item has flags, hide the item if the user has none of them set.
        // Also hide the item if hideIfFlag is set and the user has that flag.
        if (
          (hasFlags && !isFlagged) ||
          (!!item.hideIfFlag && flags[item.hideIfFlag])
        ) {
          return null;
        }

        if (item.type === 'header') {
          return <SidebarHeader key={item.label} label={item.label} />;
        }

        const itemIsActive = item.selectedFn
          ? item.selectedFn(location)
          : !!item.to &&
            location.pathname.includes(item.to.pathname || item.to);

        return (
          <SidebarItem
            component={item.component}
            key={item.label}
            label={item.label}
            Icon={item.icon}
            href={item.href}
            to={item.to}
            active={itemIsActive}
            draggable={false}
            onClick={() => {
              trackEvent('App - Navigate to Sidebar Item', {
                appSection: 'App',
                action: EVENT_ACTIONS.NAVIGATE_TO,
                target: 'Sidebar Item',
                to: item.label,
              });
            }}
          />
        );
      })}
    </Sidebar>
  );
};

export default LocalSidebar;
